import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  getPriceSheet, 
  bulkUpdateItemPrices,
  type PriceSheetItem,
  type BulkPriceUpdateRequest
} from '@/app/services/pricingService';
import BulkPriceUpdateTable from './components/BulkPriceUpdateTable';
import BulkOperationsDialog from './components/BulkOperationsDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { listSites } from '@/app/services/siteService';

type PriceType = 'sale' | 'purchase' | 'mrp' | 'cost';

export default function BulkPriceUpdatePage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  const [priceType, setPriceType] = useState<PriceType>('sale');
  const [siteId, setSiteId] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<PriceSheetItem[]>([]);
  const [modifiedItems, setModifiedItems] = useState<Map<number, number>>(new Map());
  const [isOperationsDialogOpen, setIsOperationsDialogOpen] = useState(false);

  // Fetch sites
  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 })
  });

  // Fetch price sheet data
  const { data: priceSheet = [], isLoading, error } = useQuery({
    queryKey: ['priceSheet', priceType, siteId],
    queryFn: () => getPriceSheet({ price_type: priceType, site_id: siteId }),
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: (data: BulkPriceUpdateRequest) => bulkUpdateItemPrices(data),
    onSuccess: (response) => {
      toast.success(`Successfully updated ${response.updated} prices and created ${response.created} new prices`);
      if (response.errors.length > 0) {
        response.errors.forEach(error => {
          toast.error(`Item ${error.item_id}: ${error.error}`);
        });
      }
      queryClient.invalidateQueries({ queryKey: ['priceSheet'] });
      setModifiedItems(new Map());
      setSelectedItems([]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update prices');
    },
  });

  const handlePriceChange = useCallback((itemId: number, newPrice: number) => {
    setModifiedItems(prev => {
      const updated = new Map(prev);
      updated.set(itemId, newPrice);
      return updated;
    });
  }, []);

  const handleSaveChanges = () => {
    if (modifiedItems.size === 0) {
      toast.warning('No changes to save');
      return;
    }

    const items = Array.from(modifiedItems.entries()).map(([item_id, price]) => ({
      item_id,
      price,
    }));

    bulkUpdateMutation.mutate({
      price_type: priceType,
      site_id: siteId,
      // Don't set is_tax_inclusive - let backend preserve existing value for each item
      items,
    });
  };

  const handleBulkOperation = (operation: 'percentage' | 'fixed', value: number) => {
    const itemsToUpdate = selectedItems.length > 0 ? selectedItems : priceSheet;
    const updates = new Map(modifiedItems);

    itemsToUpdate.forEach(item => {
      const currentPrice = updates.get(item.item_id) ?? parseFloat(item.price) ?? 0;
      let newPrice: number;

      if (operation === 'percentage') {
        newPrice = currentPrice * (1 + value / 100);
      } else {
        newPrice = value;
      }

      updates.set(item.item_id, Math.round(newPrice * 100) / 100);
    });

    setModifiedItems(updates);
    setIsOperationsDialogOpen(false);
    toast.success(`Applied ${operation === 'percentage' ? `${value}% change` : `fixed price ₹${value}`} to ${itemsToUpdate.length} items`);
  };

  return (
    <div className="container mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
      <Card>
        <CardHeader className="space-y-1 sm:space-y-2">
          <CardTitle className="text-xl sm:text-2xl">Bulk Price Update</CardTitle>
          <CardDescription className="text-sm">
            Update item prices in bulk. Select price type and optionally a site, then modify prices in the table below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Price Type</label>
              <Select value={priceType} onValueChange={(value: PriceType) => setPriceType(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">Sale Price</SelectItem>
                  <SelectItem value="purchase">Purchase Price</SelectItem>
                  <SelectItem value="mrp">MRP</SelectItem>
                  <SelectItem value="cost">Cost Price</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Site (Optional)</label>
              <Select value={siteId?.toString() ?? 'all'} onValueChange={(value) => setSiteId(value === 'all' ? null : parseInt(value))}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sitesData?.items?.map(site => (
                    <SelectItem key={site.id} value={site.id.toString()}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-1">
              <Button 
                variant="outline"
                size="sm"
                className="flex-1 lg:flex-none"
                onClick={() => setIsOperationsDialogOpen(true)}
                disabled={priceSheet.length === 0}
              >
                <span className="hidden sm:inline">Bulk</span> Operations
              </Button>
              <Button 
                size="sm"
                className="flex-1 lg:flex-none"
                onClick={handleSaveChanges}
                disabled={modifiedItems.size === 0 || bulkUpdateMutation.isPending}
              >
                {bulkUpdateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <span className="hidden sm:inline">Save Changes</span>
                <span className="sm:hidden">Save</span> ({modifiedItems.size})
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 sm:p-4 rounded-md text-sm">
              Failed to load price sheet: {(error as any).message}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center h-32 sm:h-64">
              <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
            </div>
          ) : (
            <div className="w-full overflow-hidden">
              <BulkPriceUpdateTable
                data={priceSheet}
                modifiedItems={modifiedItems}
                selectedItems={selectedItems}
                onPriceChange={handlePriceChange}
                onSelectionChange={setSelectedItems}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <BulkOperationsDialog
        open={isOperationsDialogOpen}
        onOpenChange={setIsOperationsDialogOpen}
        onApplyOperation={handleBulkOperation}
        selectedCount={selectedItems.length}
        totalCount={priceSheet.length}
      />
    </div>
  );
}